import { useMutation } from '@tanstack/vue-query'
import { log } from 'src/services/app/log.service'
import { notifyService } from 'src/services/app/notify.service'
import { authService } from 'src/services/api/auth.service'
import { MUTATION_KEYS } from 'src/services/consts/queries.const'
import { resolveLeaveAccountErrorMessage } from 'src/services/helpers/auth.helper'
import { wrapMutationAsync } from 'src/services/helpers/util.helper'
import { useAuthStore } from 'src/stores/auth'

/**
 * Leave-account mutation for account settings.
 *
 * Persists via `authService.leaveAccount` (POST /me/leave-account), then
 * tears down the session via `authStore.logout({ authError: true })`.
 * Cross-tab sync is handled by logout's `BROADCAST_TOPICS.LOGOUT` post.
 *
 * Consumed via `useAccountActionsStore` — components never call this
 * composable directly (project mutation convention).
 */
export function useLeaveAccountMutation() {
  const authStore = useAuthStore()

  const leaveAccountMutation = useMutation({
    mutationKey: [MUTATION_KEYS.LEAVE_ACCOUNT],
    mutationFn: () => authService.leaveAccount(),

    onSuccess: async () => {
      // Skip POST /logout. Membership is already revoked, so that call errors.
      // Auth0 logout and the local session clear still run.
      await authStore.logout({ authError: true })
    },

    onError: (error) => {
      log.error('[Account] leaveAccount failed', error)
      notifyService.error(resolveLeaveAccountErrorMessage(error))
    },
  })

  return {
    leaveAccount: wrapMutationAsync(leaveAccountMutation.mutateAsync),
    isLeavingAccount: leaveAccountMutation.isPending,
    leaveAccountError: leaveAccountMutation.error,
  }
}
